import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemOption,
  IonItemOptions,
  IonItemSliding,
  IonLabel,
  IonList,
  IonReorder,
  IonReorderGroup,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { Formik, FormikHelpers, useField, useFormikContext } from 'formik';
import { add, close, copy } from 'ionicons/icons';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import { useGetUnitOfMeasureSystem } from '../../../../../../../api/hooks/user-preference.hooks';
import { RightAlignedButtons } from '../../../../../../../components/button/aligned-buttons';
import { BaseButton } from '../../../../../../../components/button/base-button';
import { IonFormExternalPicker } from '../../../../../../../components/form/ion-form-external-picker';
import { FullScreenIonModal } from '../../../../../../../components/modal/full-screen-ion-modal';
import { SaveStatusToast } from '../../../../../../../components/toast/toast.gql';
import { ContentWithFooter } from '../../../../../../../components/ui/content-with-footer';
import { isLocalDev } from '../../../../../../../config/config';
import i18n, { i18nKeyType } from '../../../../../../../i18n';
import {
  CaptureWaypointButton,
  CaptureWaypointButtonRef,
} from '../../../../../shared/component/button/capture-waypoint-button';
import { WaitForOptions } from '../../../../../shared/component/teach/option/wait-for-options';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import { WaypointCaptureRef } from '../../../../../shared/hook/waypoint-capture.hooks';
import {
  FlowItem,
  FlowItemForm,
  FlowItemParameterRef,
  WaitForInputType,
} from '../../../../../shared/model/flow.types';
import { getParameterValue } from '../../../../../shared/util/flow-parameter.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { MultipassPostActionCopyModal } from './multipass-post-action-copy-modal';
import { useMultipassPostActionCopy } from './use-multipass-post-action-copy';

const POST_ACTION_REFS = [
  BeaconWeldingData.FlowItemRef.WAIT_FOR,
  BeaconWeldingData.FlowItemRef.MOVE,
  BeaconWeldingData.FlowItemRef.MOVE_HOME,
];

interface MultipassSchedulePostActionInputProps {
  name: string;
  disabled?: boolean;
  presentingEl?: HTMLElement;
  isFullScreen?: boolean;
}

const MultipassSchedulePostActionInput = (
  props: MultipassSchedulePostActionInputProps
): ReactElement => {
  const { name, disabled, presentingEl, isFullScreen } = props;
  const [field, _meta, helper] = useField<
    FlowItem<BeaconWeldingData.FlowItemRef>[] | undefined
  >(name);
  const { flowItemService } = useFlowServices();
  const system = useGetUnitOfMeasureSystem();
  const { availablePasses } = useMultipassPostActionCopy(name);
  const existingItems = useMemo(() => field.value || [], [field.value]);

  const [showPostActionModal, setShowPostActionModal] = useState(false);
  const [showManageActionsModal, setShowManageActionsModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedPostAction, setSelectedPostAction] =
    useState<BeaconWeldingData.FlowItemRef>(
      BeaconWeldingData.FlowItemRef.WAIT_FOR
    );
  const [schema, setSchema] = useState<yup.AnySchema | undefined>(undefined);

  useEffect(() => {
    if (editingIndex !== null && existingItems[editingIndex]) {
      setSelectedPostAction(
        existingItems[editingIndex].ref as BeaconWeldingData.FlowItemRef
      );
    }
  }, [editingIndex, existingItems]);

  const isEditing = useMemo(() => {
    return editingIndex !== null;
  }, [editingIndex]);

  const initialFormValues: FlowItemForm = useMemo(() => {
    if (editingIndex !== null && existingItems[editingIndex]) {
      return flowItemService.convertFlowItemToForm(
        existingItems[editingIndex],
        system
      );
    }

    const item = flowItemService.getDefaultItemForRef(selectedPostAction);
    const itemName = flowItemService.generateFlowItemName(item.ref);
    item.name = itemName;
    return flowItemService.convertFlowItemToForm(item, system);
  }, [
    editingIndex,
    existingItems,
    flowItemService,
    selectedPostAction,
    system,
  ]);

  const handleReorder = (event: CustomEvent) => {
    const newItems = [...existingItems];
    const from = event.detail.from;
    const to = event.detail.to;
    const item = newItems.splice(from, 1)[0];
    newItems.splice(to, 0, item);
    helper.setValue(newItems);
    event.detail.complete();
  };

  const handleDelete = (index: number) => {
    const newItems = [...existingItems];
    newItems.splice(index, 1);
    helper.setValue(newItems.length > 0 ? newItems : undefined);
  };

  const actionsLabel = useMemo(() => {
    if (existingItems.length === 0) {
      return i18n.t('common:action');
    }
    return existingItems.length === 1
      ? i18n.t('common:action')
      : i18n.t('common:actions');
  }, [existingItems]);

  return (
    <>
      <IonFormExternalPicker
        selectedName={
          existingItems.length > 0
            ? `${existingItems.length} ${actionsLabel}`
            : undefined
        }
        placeholder={i18n.t('common:actions')}
        onClick={() => {
          setShowManageActionsModal(true);
        }}
        onUnset={() => {
          helper.setValue(undefined);
        }}
        disabled={disabled}
        ionItemProps={{
          lines: 'none',
        }}
      />

      {/* Edit/Add Action Modal */}
      <FullScreenIonModal
        isOpen={showPostActionModal}
        onDidDismiss={() => {
          setShowPostActionModal(false);
          setEditingIndex(null);
        }}
        presentingElement={presentingEl}
        isFullScreen={isFullScreen}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons
              slot={'start'}
              onClick={() => {
                setShowPostActionModal(false);
                setEditingIndex(null);
              }}
            >
              <IonButton>
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{i18n.t('common:post_action')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <Formik
          initialValues={initialFormValues}
          onSubmit={(values) => {
            const flowItem = flowItemService.convertFormToFlowItem(
              values,
              system
            );
            if (!flowItem.id) {
              flowItem.id = uuid();
            }

            const newItems = [...existingItems];
            if (editingIndex !== null) {
              newItems[editingIndex] =
                flowItem as FlowItem<BeaconWeldingData.FlowItemRef>;
            } else {
              newItems.push(
                flowItem as FlowItem<BeaconWeldingData.FlowItemRef>
              );
            }
            helper.setValue(newItems);

            setShowPostActionModal(false);
            setEditingIndex(null);
          }}
          enableReinitialize={true}
          validationSchema={schema}
        >
          <MultipassSchedulePostActionSettingsForm
            isEditing={isEditing}
            selectedPostAction={selectedPostAction}
            setSelectedPostAction={setSelectedPostAction}
            setSchema={setSchema}
            onSave={() => {
              setShowPostActionModal(false);
              setEditingIndex(null);
            }}
          />
        </Formik>
      </FullScreenIonModal>

      {/* Manage Actions Modal */}
      <FullScreenIonModal
        isOpen={showManageActionsModal}
        onDidDismiss={() => {
          setShowManageActionsModal(false);
        }}
        presentingElement={presentingEl}
        isFullScreen={isFullScreen}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowManageActionsModal(false)}>
                <IonIcon icon={close} size="large" />
              </IonButton>
            </IonButtons>
            <IonTitle>{i18n.t('common:actions')}</IonTitle>
            <IonButtons slot="end">
              {existingItems.length > 0 && availablePasses.length > 0 && (
                <IonButton
                  onClick={() => setShowCopyModal(true)}
                  disabled={disabled}
                >
                  <IonIcon icon={copy} />
                </IonButton>
              )}
              <IonButton
                onClick={() => {
                  setEditingIndex(null);
                  setShowPostActionModal(true);
                }}
              >
                <IonIcon icon={add} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {existingItems.length === 0 ? (
            <div className="ion-text-center ion-padding">
              <p>{i18n.t('common:no_actions_added')}</p>
              <IonButton
                onClick={() => {
                  setEditingIndex(null);
                  setShowPostActionModal(true);
                }}
              >
                <IonIcon slot="start" icon={add} />
                {i18n.t('common:add_post_action')}
              </IonButton>
            </div>
          ) : (
            <IonList>
              <IonReorderGroup
                disabled={disabled}
                onIonItemReorder={handleReorder}
              >
                {existingItems.map((item, index) => (
                  <IonItemSliding key={item.id}>
                    <IonItem>
                      <IonReorder slot="start" />
                      <IonLabel
                        onClick={() => {
                          setEditingIndex(index);
                          setShowPostActionModal(true);
                        }}
                      >
                        {getPostActionItemLabel(item)}
                      </IonLabel>
                    </IonItem>
                    <IonItemOptions side="end">
                      <IonItemOption
                        color="danger"
                        onClick={() => handleDelete(index)}
                      >
                        {i18n.t('common:delete')}
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                ))}
              </IonReorderGroup>
            </IonList>
          )}
        </IonContent>
      </FullScreenIonModal>

      <MultipassPostActionCopyModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        currentFieldName={name}
        presentingEl={presentingEl}
      />
    </>
  );
};

interface MultipassSchedulePostActionSettingsFormProps {
  isEditing: boolean;
  selectedPostAction: BeaconWeldingData.FlowItemRef;
  setSelectedPostAction: (value: BeaconWeldingData.FlowItemRef) => void;
  setSchema: (schema: yup.AnySchema) => void;
  onSave: () => void;
}

const MultipassSchedulePostActionSettingsForm = (
  props: MultipassSchedulePostActionSettingsFormProps
): ReactElement => {
  const {
    isEditing,
    selectedPostAction,
    setSelectedPostAction,
    setSchema,
    onSave,
  } = props;
  const { flowItemService, flow } = useFlowServices();
  const { dirty, isValid, values, validateForm, submitForm } =
    useFormikContext<FlowItemForm>();
  const system = useGetUnitOfMeasureSystem();

  const setValidationSchema = useCallback(
    (schema: yup.AnySchema) => {
      setSchema(schema);
      setTimeout(() => {
        validateForm();
      }, 1);
    },
    [setSchema, validateForm]
  );

  useEffect(() => {
    const schema = flowItemService.getYupSchemaForItem(values.ref, system, {
      flow,
      form: values,
    });

    setValidationSchema(schema);
  }, [
    flow,
    flowItemService,
    setSchema,
    setValidationSchema,
    system,
    validateForm,
    values,
  ]);

  const canSubmit = useMemo(() => {
    const isChanged = isEditing ? dirty : true;
    return isChanged && isValid;
  }, [dirty, isEditing, isValid]);

  const items = flowItemService
    .getItemsMetadata()
    .filter((item) =>
      POST_ACTION_REFS.includes(item.ref as BeaconWeldingData.FlowItemRef)
    );

  const selectOptions: {
    label: i18nKeyType;
    value: BeaconWeldingData.FlowItemRef;
  }[] = useMemo(() => {
    return items.map((item) => ({
      label: item.name,
      value: item.ref as BeaconWeldingData.FlowItemRef,
    }));
  }, [items]);

  const footer = useMemo(() => {
    return (
      <MultipassSchedulePostActionSettingsFooter
        disabled={!canSubmit}
        onSave={() => {
          submitForm();
          onSave();
        }}
      />
    );
  }, [canSubmit, onSave, submitForm]);

  return (
    <ContentWithFooter footer={footer}>
      <>
        <IonItem lines="full" className="transparent-background">
          <IonSelect
            label="Action Type"
            value={selectedPostAction}
            onIonChange={(e) => {
              const value = e.detail.value;
              setSelectedPostAction(value);
            }}
          >
            {selectOptions.map((option) => (
              <IonSelectOption key={option.value} value={option.value}>
                {i18n.t(option.label)}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
        <MultipassSchedulePostActionSettings itemRef={selectedPostAction} />
      </>
    </ContentWithFooter>
  );
};

interface MultipassSchedulePostActionSettingsFooterProps {
  disabled: boolean;
  onSave: () => void;
}

const MultipassSchedulePostActionSettingsFooter = (
  props: MultipassSchedulePostActionSettingsFooterProps
): ReactElement => {
  const { disabled, onSave } = props;

  return (
    <IonToolbar>
      <RightAlignedButtons>
        <BaseButton
          buttonProps={{
            color: 'primary',
            disabled,
            onClick: () => {
              onSave();
            },
          }}
        >
          {i18n.t('common:set_action')}
        </BaseButton>
      </RightAlignedButtons>
    </IonToolbar>
  );
};

interface MultipassSchedulePostActionSettingsProps {
  itemRef: BeaconWeldingData.FlowItemRef;
}

const MultipassSchedulePostActionSettings = (
  props: MultipassSchedulePostActionSettingsProps
) => {
  const { itemRef } = props;
  const { setFieldValue } = useFormikContext<FlowItemForm>();

  if (itemRef === BeaconWeldingData.FlowItemRef.WAIT_FOR) {
    return <WaitForOptions />;
  } else if (itemRef === BeaconWeldingData.FlowItemRef.MOVE) {
    return <AirMoveOptions setFieldValue={setFieldValue} />;
  }

  return null;
};

interface AirMoveOptionsProps {
  setFieldValue: FormikHelpers<FlowItemForm>['setFieldValue'];
}

enum CaptureState {
  IDLE = 'idle',
  WAITING = 'waiting',
  CAPTURED = 'captured',
  CANCELED = 'canceled',
}

const AirMoveOptions = (props: AirMoveOptionsProps): ReactElement => {
  const { setFieldValue } = props;
  const { values } = useFormikContext<FlowItemForm>();

  const [captureState, setCaptureState] = useState<CaptureState>(
    CaptureState.IDLE
  );

  const innerRef = useRef<WaypointCaptureRef>({} as WaypointCaptureRef);
  const captureButtonRef = useRef<CaptureWaypointButtonRef>(null);

  const isCapturing = useMemo(() => {
    return captureState === 'waiting';
  }, [captureState]);

  const setValueForParam = useMemo(
    () =>
      createValueSetterForParameter<BeaconWeldingData.FlowItemParameterRef>(
        setFieldValue
      ),
    [setFieldValue]
  );

  const isDev = isLocalDev();

  // Auto-click the capture button when component mounts for new air move actions
  useEffect(() => {
    // Check if waypoint data already exists
    const existingWaypoint =
      values.parameters?.[BeaconWeldingData.FlowItemParameterRef.WAYPOINT]
        ?.value;

    // Only auto-click if no waypoint data exists
    if (!existingWaypoint) {
      const timer = setTimeout(() => {
        if (captureButtonRef.current) {
          captureButtonRef.current.click();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [values.parameters]);

  return (
    <>
      <IonItem lines="full" className="transparent-background">
        <IonLabel>{i18n.t('common:point')}</IonLabel>
        <CaptureWaypointButton
          ref={captureButtonRef}
          captureButtonText={i18n.t('common:capture_point')}
          cancelButtonText={i18n.t('common:cancel')}
          onCapture={(waypoint, qnear) => {
            setValueForParam(
              BeaconWeldingData.FlowItemParameterRef.WAYPOINT,
              waypoint
            );
            setValueForParam(
              BeaconWeldingData.FlowItemParameterRef.QNEAR,
              qnear
            );
            setCaptureState(CaptureState.CAPTURED);
          }}
          onCaptureStart={() => setCaptureState(CaptureState.WAITING)}
          onCaptureCancel={() => setCaptureState(CaptureState.CANCELED)}
          innerRef={innerRef}
        />
        {/* This allows us to simulate a press in development */}
        {isDev && isCapturing && (
          <BaseButton
            buttonProps={{
              onClick: () => {
                innerRef.current?.forcePress();
              },
            }}
          >
            Simulate Capture
          </BaseButton>
        )}
      </IonItem>
      <SaveStatusToast
        loading={captureState === 'waiting'}
        error={captureState === 'canceled'}
        onDidDismiss={() => {
          setCaptureState(CaptureState.IDLE);
        }}
        showOnly="success"
        defaultMessageKey="common:waypoint_captured"
        position="top"
      />
    </>
  );
};

const PARAMETER_LIST_FORM_PREFIX = 'parameters';

type ValueSetter<Ref> = (ref: Ref, value: unknown) => void;

const createValueSetterForParameter = <
  Ref extends FlowItemParameterRef = FlowItemParameterRef,
>(
  setFieldValue: FormikHelpers<unknown>['setFieldValue']
): ValueSetter<Ref> => {
  return (ref: Ref, value: unknown) => {
    setFieldValue(`${PARAMETER_LIST_FORM_PREFIX}.${ref}.value`, value);
    setFieldValue(`${PARAMETER_LIST_FORM_PREFIX}.${ref}.baseValue`, value);
  };
};

const getPostActionItemLabel = (
  flowItem: FlowItem<BeaconWeldingData.FlowItemRef>
): string => {
  const { ref } = flowItem;

  if (ref === BeaconWeldingData.FlowItemRef.WAIT_FOR) {
    return getWaitForLabel(flowItem);
  } else if (ref === BeaconWeldingData.FlowItemRef.MOVE) {
    return i18n.t('common:air_move');
  } else if (ref === BeaconWeldingData.FlowItemRef.MOVE_HOME) {
    return i18n.t('common:move_home');
  }

  return '';
};

const getWaitForLabel = (
  flowItem: FlowItem<BeaconWeldingData.FlowItemRef>
): string => {
  const waitForType = getParameterValue<WaitForInputType>(
    flowItem,
    BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TYPE_OF_INPUT
  );

  if (waitForType === WaitForInputType.OPERATOR_BUTTON) {
    return i18n.t('common:wait_for_operator');
  } else if (waitForType === WaitForInputType.SENSOR) {
    const portValue = getParameterValue<BeaconWeldingData.WaitForInputPort>(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_INPUT
    );
    const inputState = getParameterValue<boolean>(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_INPUT_STATE
    );

    let label: string = i18n.t('common:wait_for_input');
    const portName = i18n.t(
      BeaconWeldingData.WaitForInputPortValueToName[portValue]
    );
    const sensorState = inputState ? i18n.t('common:on') : i18n.t('common:off');

    label += ` (${portName}, ${sensorState})`;

    return label;
  } else {
    let label: string = i18n.t('common:wait_for_time');
    const waitForTime = getParameterValue<number>(
      flowItem,
      BeaconWeldingData.FlowItemParameterRef.WAIT_FOR_TIME
    );

    label += ` (${waitForTime}s)`;

    return label;
  }
};

export { MultipassSchedulePostActionInput };
